import { useMutation, useQueryClient } from '@tanstack/react-query'
import { startAuthentication } from '@simplewebauthn/browser'
import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { authApi, authKeys, isFullAuthentication, needsFullNavigation, safeNext, useAuthState, type AuthState } from '@/api/auth'
import { setActiveIdentity } from '@/api/identity'
import { ApiError } from '@/api/client'
import { Button, ErrorText, Input, PageSpinner } from '@/components/ui'
import { getLockAfter, markActive } from '@/lock/store'

import { AuthLayout, TotpCodeInput } from './parts'
import { digitsOnly } from './utils'

type Step = { kind: 'password' } | { kind: 'code'; challenge: string }

export function LoginPage() {
  const state = useAuthState()
  const location = useLocation()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<Step>({ kind: 'password' })
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [code, setCode] = useState('')
  const [notice, setNotice] = useState<string | null>((location.state as { notice?: string } | null)?.notice ?? null)
  const identityKey = !state.data ? '' : isFullAuthentication(state.data) ? `user:${state.data.user.id}:${state.data.user.legacy_owner}` : 'none'
  const [identityReadyKey, setIdentityReadyKey] = useState('')
  useEffect(() => {
    const confirmed = state.data
    if (!confirmed) return
    let current = true
    void setActiveIdentity(isFullAuthentication(confirmed) ? { id: confirmed.user.id, legacy_owner: confirmed.user.legacy_owner } : null).finally(() => {
      if (current) setIdentityReadyKey(identityKey)
    })
    return () => { current = false }
  }, [identityKey, state.data])
  const identityReady = Boolean(identityKey) && identityReadyKey === identityKey

  const passwordStep = useMutation({
    mutationFn: () => authApi.login(email.trim(), password),
    onSuccess: async (data) => {
      setNotice(null)
      setCode('')
      if (data.needs_totp && data.challenge) setStep({ kind: 'code', challenge: data.challenge })
      else await finish()
    },
  })

  const finish = async () => {
    const confirmed = await authApi.state()
    await setActiveIdentity(confirmed.user && !confirmed.user.requires_email && !confirmed.user.requires_totp_setup
      ? { id: confirmed.user.id, legacy_owner: confirmed.user.legacy_owner } : null)
    markActive()
    queryClient.setQueryData<AuthState>(authKeys.state, confirmed)
    void queryClient.invalidateQueries()
    if (needsFullNavigation(next)) window.location.assign(next)
    else navigate(next, { replace: true })
  }

  const codeStep = useMutation({
    mutationFn: (value: string) => {
      if (step.kind !== 'code') throw new Error('нет вызова')
      // Блокировка, выбранная на этом устройстве, сразу действует и для новой сессии.
      const lock_after_s = getLockAfter()
      return authApi.loginSecondFactor(
        useRecovery
          ? { challenge: step.challenge, recovery_code: value, lock_after_s }
          : { challenge: step.challenge, code: value, lock_after_s },
      )
    },
    onSuccess: () => void finish(),
    onError: (error) => {
      if (error instanceof ApiError && error.code.startsWith('challenge_')) {
        setNotice(error.message)
        setStep({ kind: 'password' })
        setPassword('')
      }
      setCode('')
    },
  })
  const passkey = useMutation({
    mutationFn: async () => {
      const { challenge, options } = await authApi.passkeyOptions(email.trim())
      const credential = await startAuthentication({ optionsJSON: options })
      const result = await authApi.loginPasskey(challenge, credential)
      if (result.needs_totp && result.challenge) {
        setStep({ kind: 'code', challenge: result.challenge })
        return
      }
      await finish()
    },
  })

  if (state.isPending) return <PageSpinner />
  if (state.data?.setup_required) return <Navigate to="/setup" replace />
  if (state.data?.authenticated && state.data.user && (state.data.user.requires_email || state.data.user.requires_totp_setup)) return <Navigate to="/account/complete" replace />
  if (isFullAuthentication(state.data) && identityReady && !codeStep.isSuccess) {
    return needsFullNavigation(next) ? null : <Navigate to={next} replace />
  }

  const submitPassword = (e: FormEvent) => {
    e.preventDefault()
    if (password) passwordStep.mutate()
  }

  const submitCode = (e: FormEvent) => {
    e.preventDefault()
    const value = useRecovery ? code.trim() : digitsOnly(code)
    if (value) codeStep.mutate(value)
  }

  if (step.kind === 'password') {
    return (
      <AuthLayout title="Вход" description="Сервис закрыт: войди своим паролем.">
        <form onSubmit={submitPassword} className="flex flex-col gap-4" noValidate>
          <Input label="Email" type="email" name="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input
            label="Пароль"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <ErrorText>{notice}</ErrorText>
          <ErrorText error={passwordStep.error} />
          <ErrorText error={passkey.error} />
          <Button type="submit" variant="primary" size="lg" block loading={passwordStep.isPending} disabled={!password}>
            Дальше
          </Button>
        </form>
        <Button variant="secondary" size="lg" block className="mt-3" loading={passkey.isPending} disabled={!email.trim()} onClick={() => passkey.mutate()}>
          Войти с ключом доступа
        </Button>
        <Button variant="ghost" size="sm" className="mt-2" onClick={() => navigate(`/forgot-password?email=${encodeURIComponent(email)}`)}>Забыли пароль?</Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Вход"
      description={
        useRecovery
          ? 'Введи один из кодов восстановления. Каждый код работает один раз.'
          : 'Введи 6-значный код из приложения-аутентификатора.'
      }
    >
      <form onSubmit={submitCode} className="flex flex-col gap-4" noValidate>
        {useRecovery ? (
          <Input
            key="recovery"
            label="Код восстановления"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            placeholder="xxxxx-xxxxx"
            className="font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        ) : (
          <TotpCodeInput
            key="totp"
            label="Код 2FA"
            autoFocus
            value={code}
            onChange={(e) => {
              const value = e.target.value
              setCode(value)
              if (digitsOnly(value).length === 6 && !codeStep.isPending) codeStep.mutate(digitsOnly(value))
            }}
          />
        )}
        <ErrorText error={codeStep.error} />
        <Button type="submit" variant="primary" size="lg" block loading={codeStep.isPending} disabled={!code.trim()}>
          Войти
        </Button>
        <div className="flex flex-wrap justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setUseRecovery((v) => !v)
              setCode('')
              codeStep.reset()
            }}
          >
            {useRecovery ? 'Ввести код из приложения' : 'Нет доступа к приложению'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStep({ kind: 'password' })
              setPassword('')
              codeStep.reset()
            }}
          >
            Другой пароль
          </Button>
        </div>
      </form>
    </AuthLayout>
  )
}
